import { useState, KeyboardEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './EmailTagInput.module.scss';

interface EmailTagInputProps {
  label: string;
  emails: string[];
  onChange: (emails: string[]) => void;
  placeholder?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const EmailTagInput = ({
  label,
  emails,
  onChange,
  placeholder,
}: EmailTagInputProps) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const addEmail = () => {
    const email = inputValue.trim().toLowerCase();

    if (!email) return;

    if (!EMAIL_REGEX.test(email)) {
      setError(t('email_routing_invalid_email'));
      return;
    }

    if (emails.includes(email)) {
      setError(t('email_routing_duplicate_email'));
      return;
    }

    onChange([...emails, email]);
    setInputValue('');
    setError('');
  };

  const removeEmail = (emailToRemove: string) => {
    onChange(emails.filter(e => e !== emailToRemove));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEmail();
    }

    if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
      removeEmail(emails[emails.length - 1]);
    }
  };

  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  return (
    <div className={styles.wrapper}>
      <span className={styles.label}>{label}</span>
      <div className={styles.tagsContainer} onClick={handleContainerClick}>
        {emails.map(email => (
          <span key={email} className={styles.tag}>
            {email}
            <button
              type="button"
              className={styles.tagRemove}
              onClick={e => {
                e.stopPropagation();
                removeEmail(email);
              }}
            >
              &times;
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          className={styles.input}
          type="email"
          value={inputValue}
          onChange={e => {
            setInputValue(e.target.value);
            setError('');
          }}
          onKeyDown={handleKeyDown}
          onBlur={addEmail}
          placeholder={emails.length === 0 ? (placeholder || t('email_routing_add_email')) : ''}
        />
      </div>
      {error && <span className={styles.error}>{error}</span>}
    </div>
  );
};
